import { action, observable, makeObservable } from 'mobx';
import { apiCaller } from '@services/apiCaller';
import { webappClient } from '@lib/bugsnag';
import { IRootStore } from '@stores/index';
import orderBy from 'lodash/orderBy';
import { Group, Schema } from '@components/common/formSchema';
import { DefinitionHiddenOption, DestinationDefinitionCategory, RudderScope } from './types';
import { DestUIConfigV2 } from '@components/destinations/destination-view/configurationV2/formComponents/types';
import { MESSAGE_TYPE } from './source/types';

export interface IDestinationDefsListStore {
  destinationDefs: IDestDefinition[];
  rootStore: IRootStore;
  getDestinationDefs(): void;
  getDestinationDef(id: string): IDestDefinition | undefined;
  getDestinationDefByName(name: string): IDestDefinition | undefined;
  getCurrentMajorVersion(id: string): number | undefined;
}

interface IAuthConfig {
  provider?: 'Google';
  type: string;
  secretFields: unknown[];
  nameField: string;
  config: Schema;
  role: string;
  rudderScopes?: RudderScope[];
}

export type ConnectionMode = 'cloud' | 'device' | 'hybrid';

export interface Config {
  supportedAccountDefinitions: {
    rudderAccountId: string[];
    rudderDeleteAccountId?: string[];
  };
  supportedSourceTypes: string[];
  supportedConnectionModes: {
    [key: string]: ConnectionMode[];
  };
  syncBehaviours: string[];
  destConfig: {
    defaultConfig: string[];
    [key: string]: string[];
  };
  preview: boolean;
  auth: IAuthConfig;
  deleteSupport: boolean;
  supportsVisualMapper: boolean;
  supportsVisualMapperV2?: boolean;
  supportsDeliveredWithWarnings?: boolean;
  isAudienceSupported: boolean;
  disableJsonMapper?: boolean;
  supportsBlankAudienceCreation?: boolean;
  immutableKeys?: string[];
  supportedMessageTypes?: {
    cloud: MESSAGE_TYPE[];
  };
}

interface DestinationFeatures {
  vdmLabels: {
    newAudienceAlternateLabel: string;
    existingAudienceAlternateLabel: string;
    audienceAlternateLabel: string;
  };
}
export interface IDestDefinition {
  id: string;
  name: string;
  displayName: string;
  category: DestinationDefinitionCategory;
  config: Config;
  options: {
    createFlow?: {
      sourceSelectionOptional?: boolean;
    };
    isBeta?: boolean;
    legacy?: {
      notice?: string;
      selectionNotice?: string;
      migrationGuide?: string;
    };
    hidden?: DefinitionHiddenOption;
    deprecated?: boolean;
    deprecationLabel?: string;
    destinationFeatures?: DestinationFeatures;
    supportsCustomMappings?: boolean;
    secondPositionRedirectComponent?: boolean;
    disableLiveEvents?: boolean;
  } | null;
  isPopular: boolean;
  uiConfig: Group[] | DestUIConfigV2;
  /**
   * The definition's current major, as an integer — the value a destination create sends as
   * `version`. Served by `/web/destination-definitions`, which parses it from the stored
   * `major.minor` so the minor never reaches us.
   *
   * Optional because this interface also types the definition embedded in a destination read, which
   * carries the pinned major's slice and deliberately omits the definition's own version. Callers
   * must treat it as present only on a definition that came from the defs list.
   */
  version?: number;
}

const popularDestinations = [
  'WEBHOOK',
  'S3',
  'AM',
  'BQ',
  'POSTGRES',
  'SNOWFLAKE',
  'MP',
  'RS',
  'HS',
];

export class DestinationDefsListStore implements IDestinationDefsListStore {
  @observable public destinationDefs: IDestDefinition[] = [];

  @observable public rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action.bound
  public async getDestinationDefs() {
    const res = await apiCaller().get<Omit<IDestDefinition, 'isPopular'>[]>(
      '/web/destination-definitions',
    );
    this.destinationDefs = orderBy(
      res.data.map((x) => ({
        id: x.id,
        name: x.name,
        displayName: x.displayName,
        category: x.category,
        config: x.config,
        uiConfig: x.uiConfig,
        options: x.options,
        // Narrowed at ingest rather than trusted: the field is a plain JSON value, and a control
        // plane that predates it omits it entirely. Anything that is not a number is dropped here so
        // `version` means "the current major" everywhere downstream, or nothing at all.
        version: typeof x.version === 'number' ? x.version : undefined,
      })),
      [(dest) => dest.displayName?.toString().toLowerCase()],
      ['asc'],
    ).map((a) => ({ ...a, isPopular: popularDestinations.includes(a.name) }));

    this.reportDefinitionsWithoutMajor();
  }

  /**
   * A definition served without a current major means every create against it silently falls back to
   * the server-resolved major — the outcome sending the version explicitly exists to avoid.
   *
   * Reported here, once per load, because this is the only place that can name the anomaly: the
   * response is in hand, so "no major on the wire" is distinguishable from an unknown id or an
   * unloaded catalog, which is all a consumer sees. It also fires before any create rather than
   * alongside one, and covers every definition rather than only those a user happens to pick — so
   * "n of N" separates a control plane that does not serve the field from one bad definition row.
   */
  private reportDefinitionsWithoutMajor() {
    const withoutMajor = this.destinationDefs.filter((def) => def.version === undefined);
    if (withoutMajor.length === 0) {
      return;
    }

    webappClient.notify(
      new Error('Destination definitions served without a current major version'),
      (event) => {
        event.addMetadata('destinationDefinitions', {
          withoutMajor: withoutMajor.length,
          total: this.destinationDefs.length,
          affected: withoutMajor.map((def) => def.name),
        });
      },
    );
  }

  public getDestinationDef(id: string) {
    return this.destinationDefs.find((def: IDestDefinition) => def.id === id);
  }

  public getDestinationDefByName(name: string) {
    return this.destinationDefs.find((def: IDestDefinition) => def.name === name);
  }

  /**
   * The definition's current major — the version a create pins itself to. `undefined` when the
   * control plane does not serve one, which callers must handle rather than substitute a default
   * for: the server owns the fallback, and guessing here would pin new destinations to the wrong
   * major.
   */
  public getCurrentMajorVersion(id: string) {
    return this.getDestinationDef(id)?.version;
  }
}
